import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { profileApi } from '../../lib/api.ts'
import { useSession, SESSION_KEY } from '../../hooks/useSession.ts'
import { useTheme, type ThemePreference } from '../../hooks/useTheme.ts'
import { ErrorState, Skeleton } from '../../ui/primitives.tsx'
import { userColorVar } from '../shell/Avatar.tsx'
import { TokenSection } from './TokenSection.tsx'
import styles from './settings.module.css'

const THEMES: { value: ThemePreference; label: string }[] = [
  { value: 'dark', label: 'ciemny' },
  { value: 'light', label: 'jasny' },
  { value: 'system', label: 'systemowy' },
]

/** Settings (ui-spec §5.5). Four sections, in the order the spec lists them. */
export function SettingsView(): React.JSX.Element {
  const session = useSession()
  const { preference, setPreference } = useTheme()

  // `return null` here meant a blank page for the whole load — and permanently if the session
  // request failed, because nothing distinguished "not loaded yet" from "will never load".
  if (session.isError) {
    return (
      <div className={styles.wrap}>
        <h1 className={styles.title}>Ustawienia</h1>
        <ErrorState
          text="Nie udało się wczytać twojego profilu."
          onRetry={() => void session.refetch()}
        />
      </div>
    )
  }

  if (!session.data) {
    return (
      <div className={styles.wrap}>
        <h1 className={styles.title}>Ustawienia</h1>
        <Skeleton height={120} delay={0} />
        <Skeleton height={120} delay={200} />
      </div>
    )
  }

  const colorVar = userColorVar(session.data.id)

  return (
    <div className={styles.wrap}>
      <h1 className={styles.title}>Ustawienia</h1>

      <section className={styles.panel}>
        <div className={styles.panelHead}>
          <h2 className="section-label">Profil</h2>
        </div>

        <div className={styles.grid}>
          <span className={styles.rowLabel}>Nazwa wyświetlana</span>
          <DisplayNameField value={session.data.displayName} />

          <span className={styles.rowLabel}>E-mail</span>
          <span className={styles.readonly}>{session.data.email}</span>

          <span className={styles.rowLabel}>Kolor obecności</span>
          <span className={styles.colorRow}>
            <span
              className={styles.swatch}
              // Runtime value from data, not from the design — the exception ADR 0009 allows.
              style={{ ['--user' as string]: `var(${colorVar})` }}
            />
            <code className={styles.hex}>
              <ColorHex variable={colorVar} theme={preference} />
            </code>
            <span className={styles.hint}>kursor i awatar w całej aplikacji</span>
          </span>

          <span className={styles.rowLabel}>Hasło</span>
          <span className={styles.hint}>
            Zmiana wyłącznie z terminala VPS:{' '}
            <code className={styles.inline}>reset-password.js</code>
          </span>
        </div>
      </section>

      <TokenSection />

      <section className={styles.panel}>
        <div className={styles.panelHead}>
          <h2 className="section-label">Podłączenie agenta</h2>
        </div>
        <pre className={styles.code}>
          {`claude mcp add tracker --transport http ${window.location.origin}/mcp \\
  --header "Authorization: Bearer <token>"`}
        </pre>
        <p className={styles.hint}>Każda akcja agenta zapisuje się na konto właściciela tokena.</p>
      </section>

      <section className={styles.panel}>
        <div className={styles.panelHead}>
          <h2 className="section-label">Wygląd</h2>
        </div>
        <div className={styles.grid}>
          <span className={styles.rowLabel}>Motyw</span>
          {/* Strzałki przełączają, Tab wchodzi i wychodzi jednym stopem (roving tabindex).
              Sam `role="radiogroup"` bez tego jest gorszy niż zwykłe przyciski: obiecuje
              czytnikowi ekranu zachowanie, którego klawiatura nie realizuje. */}
          <span className={styles.segmented} role="radiogroup" aria-label="Motyw">
            {THEMES.map((theme, index) => (
              <button
                key={theme.value}
                type="button"
                role="radio"
                aria-checked={preference === theme.value}
                tabIndex={preference === theme.value ? 0 : -1}
                className={preference === theme.value ? styles.segmentOn : styles.segment}
                onClick={() => setPreference(theme.value)}
                onKeyDown={(e) => {
                  const step =
                    e.key === 'ArrowRight' || e.key === 'ArrowDown'
                      ? 1
                      : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
                        ? -1
                        : 0
                  if (step === 0) return
                  e.preventDefault()
                  const next = THEMES[(index + step + THEMES.length) % THEMES.length]
                  if (!next) return
                  setPreference(next.value)
                  // Focus follows selection, as it does in a native radio group.
                  const group = e.currentTarget.parentElement
                  const buttons = group?.querySelectorAll<HTMLButtonElement>('[role="radio"]')
                  buttons?.[(index + step + THEMES.length) % THEMES.length]?.focus()
                }}
              >
                {theme.label}
              </button>
            ))}
          </span>
        </div>
      </section>
    </div>
  )
}

/** Saves on blur, like every other inline edit in the app — no Save button anywhere (ui-spec §5.3). */
function DisplayNameField({ value }: { value: string }): React.JSX.Element {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState(value)

  useEffect(() => setDraft(value), [value])

  const rename = useMutation({
    mutationFn: (displayName: string) => profileApi.rename(displayName),
    onSuccess: ({ user }) => {
      queryClient.setQueryData(SESSION_KEY, user)
      // The name shows up next to every task and comment, so those views are stale now too.
      void queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  return (
    <input
      className={styles.field}
      aria-label="Nazwa wyświetlana"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const next = draft.trim()
        if (next && next !== value) rename.mutate(next)
        else setDraft(value)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur()
        if (e.key === 'Escape') {
          setDraft(value)
          e.currentTarget.blur()
        }
      }}
    />
  )
}

/**
 * Reads the person's colour out of the stylesheet instead of duplicating the hex here — the value
 * lives in tokens.css and nowhere else (RULES §5), and it has to change with the theme.
 */
function ColorHex({
  variable,
  theme,
}: {
  variable: string
  /** Not read — it is the dependency that makes the value re-resolve after a theme switch. */
  theme: ThemePreference
}): React.JSX.Element {
  const [hex, setHex] = useState('')

  useEffect(() => {
    const value = getComputedStyle(document.documentElement).getPropertyValue(variable)
    setHex(value.trim())
  }, [variable, theme])

  return <>{hex}</>
}
